'use client';

import React from 'react';

interface Challenge {
  term: string;
  definition: string;
  description: string;
}

const ProblemStatement = () => {
  const challenges: Challenge[] = [
    {
      term: 'Adoption Debt',
      definition: 'The growing gap between AI capability and actual organisational use',
      description: 'Organisations invest heavily in AI technology, yet struggle to translate that investment into widespread adoption. The gap between what AI can do and what organisations actually use it for continues to widen — not due to technical limitations, but organisational readiness.',
    },
    {
      term: 'Pilot Graveyards',
      definition: 'AI pilots that demonstrate promise but never scale beyond initial deployment',
      description: 'Successful proof-of-concepts fail to transition into production. Organisations accumulate dozens of pilots that worked technically but never achieved enterprise-wide adoption due to governance gaps, misalignment, or lack of change capacity.',
    },
    {
      term: 'Leadership Latency',
      definition: 'The delay between AI potential and organisational readiness to act',
      description: 'Leadership teams recognise AI\'s transformative potential but lack the frameworks, governance structures, and change leadership capabilities to drive continuous transformation at the pace AI demands. This latency creates risk and missed opportunity.',
    },
  ];

  return (
    <section className="py-24 lg:py-32 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Section header */}
          <div className="mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-headline text-[#0A2540] mb-6 leading-tight">
              Why AI Change Is Difficult
            </h2>
            <p className="text-xl text-gray-700 font-body leading-relaxed">
              Organisations struggle with AI adoption not because of technology limitations, but because of systemic leadership and governance challenges that require fundamentally different approaches to change.
            </p>
          </div>

          {/* Challenges */}
          <div className="space-y-12">
            {challenges.map((challenge, index) => (
              <div key={index} className="border-l-4 border-[#0A2540] pl-8 py-2">
                <h3 className="text-2xl font-bold font-headline text-[#0A2540] mb-3">
                  {challenge.term}
                </h3>
                <p className="text-lg font-semibold text-gray-800 font-body mb-4 italic">
                  {challenge.definition}
                </p>
                <p className="text-base text-gray-700 font-body leading-relaxed">
                  {challenge.description}
                </p>
              </div>
            ))}
          </div>

          {/* Closing statement */}
          <div className="mt-16 p-8 bg-white border-2 border-gray-200">
            <p className="text-lg text-gray-800 font-body leading-relaxed">
              These are not execution failures. They are <span className="font-bold">systemic leadership challenges</span> that require governance, alignment, and change leadership capabilities designed for continuous transformation.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemStatement;